import { iono } from 'global';
import Slider from './slider';
import { enableAttribute, resizeObserver } from '../../utils/dom';
import Component from './component';
import { supportsMouseInput, supportsTouchInput } from '../utils/support';
import { rgbToCss } from '../utils/color';
import Waveform from './waveform';
import { normalize, avg } from '../../../utils/math';
import WaveformLoader from '../utils/waveform-loader';
import { destroyTooltip, updateTooltip } from '../utils/tooltip';

/**
 * Find a waveform link in a playlist item
 *
 * @param  {Object} playlistItem
 * @return {String|null}
 */
function findWaveformUrl (playlistItem) {
  for (let i = 0, len = playlistItem.links.length; i < len; i++) {
    if (playlistItem.links[i].kind === 'waveform') {
      return playlistItem.links[i].url;
    }
  }
  return null;
}

const FPS_LIMIT = 30.0;
const FPS_LIMIT_TOLERANCE = 0.001;
const LIVE_WAVEFORM_CUTOFF_PERCENT = 60;

// @fix disable left/right arrow until seek is available
class TimelineSlider extends Slider {
  constructor (options) {
    super(options);

    this.tracks = [];
  }

  getDefaultProps () {
    return Object.assign(super.getDefaultProps(), {
      locked: true,
      progress: 0,
      chapters: []
    });
  }

  render () {
    return `<div class="iono-slider iono-timeline-slider" tabindex="0" title="00:00" data-tooltip-follow="x">
      <button class="iono-slider-thumb" tabindex="-1"></button>
      <div class="iono-timeline-tracks">
        <div class="iono-slider-track">
          <div class="iono-slider-bar iono-timeline-position"></div>
          <div class="iono-slider-bar iono-timeline-progress"></div>
        </div>
      </div>
    </div>`;
  }

  renderChapter (chapter) {
    const $track = document.createElement('div');
    const $trackPosition = document.createElement('div');
    const $trackProgress = document.createElement('div');

    $track.className = 'iono-slider-track iono-timeline-track';
    $trackPosition.className = 'iono-slider-bar iono-timeline-position';
    $trackProgress.className = 'iono-slider-bar iono-timeline-progress';

    const trackWidth = (chapter.endTime - chapter.startTime) / this.props.max * 100;
    $track.setAttribute('data-start-time', chapter.startTime);
    $track.setAttribute('data-end-time', chapter.endTime);
    $track.setAttribute('data-title', chapter.text);
    $track.style.width = `${trackWidth}%`;

    $track.appendChild($trackProgress);
    $track.appendChild($trackPosition);

    return {
      elements: {
        track: $track,
        position: $trackPosition,
        progress: $trackProgress
      },
      chapter: chapter
    };
  }

  attach (element) {
    super.attach(element);

    this.$trackContainer = this.$('.iono-timeline-tracks');

    this.update();
  }

  update (changed = []) {
    if (!this.isAttached()) return;

    if (changed.includes('chapters') || changed.includes('max')) {
      this.updateChapters();
    }

    if (changed.includes('progress')) {
      this._updateProgress();
    }

    return super.update(changed);
  }

  updateChapters () {
    const chapters = this.props.chapters.length
      ? this.props.chapters
      : [{
          startTime: 0,
          endTime: this.props.max
        }];

    this.tracks = [];
    this.$trackContainer.innerHTML = '';

    const lastIndex = chapters.length - 1;
    for (const [index, chapter] of chapters.entries()) {
      // increase first chapter to include audio from position 0 to the chapter start
      if (index === 0 && chapter.startTime !== 0) {
        chapter.startTime = 0;
      }

      // increase the last chapter to include from the chapters end position to the total audio duration
      if (index === lastIndex && chapter.endTime < this.props.max) {
        chapter.endTime = this.props.max;
      }

      const track = this.renderChapter(chapter);

      this.tracks.push(track);
      this.$trackContainer.appendChild(track.elements.track);
    }

    this._drawValue();
    this._updateProgress();
  }

  onslidestart (event) {
    if (this.props.locked) {
      event.preventDefault();
      return false;
    }

    super.onslidestart(event);
  }

  onkeydown (event) {
    if (this.props.locked && this.isSliderKey(event.keyCode)) {
      event.preventDefault();
    } else {
      super.onkeydown(event);
    }
  }

  _drawValue (value = this.props.value) {
    if (!this.isAttached()) return;

    for (const track of this.tracks) {
      const $position = track.elements.position;

      // don't draw chapter position until a duration exists
      if (this.props.max === 0) {
        $position.style.transform = 'scale(0, 1)';
        continue;
      }

      const { startTime, endTime } = track.chapter;

      if (value >= endTime) {
        $position.style.transform = 'scale(1, 1)';
      } else if (startTime <= value && endTime >= value) {
        const fillPercent = (value - startTime) / (endTime - startTime);
        $position.style.transform = `scale(${fillPercent}, 1)`;
      } else {
        $position.style.transform = 'scale(0, 1)';
      }
    }

    const thumbPosition = value / this.props.max * 100;
    this.$thumb.style.left = `${thumbPosition}%`;
    this.$element.setAttribute('aria-valuenow', value);
  }

  _updateProgress (progress = this.props.progress) {
    if (!this.isAttached()) return;

    const progressPosition = this.props.max * (progress / 100);

    for (const track of this.tracks) {
      const $progress = track.elements.progress;

      // don't draw chapter progress until a duration exists
      if (this.props.max === 0) {
        $progress.style.transform = 'scale(0, 1)';
        continue;
      }

      const { startTime, endTime } = track.chapter;

      if (progressPosition >= endTime) {
        $progress.style.transform = 'scale(1, 1)';
      } else if (startTime <= progressPosition && endTime >= progressPosition) {
        const fillPercent = (progressPosition - startTime) / (endTime - startTime);
        $progress.style.transform = `scale(${fillPercent}, 1)`;
      } else {
        $progress.style.transform = 'scale(0, 1)';
      }
    }
  }
}

export default class Timeline extends Component {
  constructor (options) {
    super(options);

    this.player = this.props.player;

    this._audioContext = null;
    this._audioAnalyser = null;
    this._audioSource = null;
    this._audioAnalyserRunning = false;

    this._slider = new TimelineSlider({
      // @todo determine the keyStep value based on audio duration, shorter files should step by a lower value (5 - 15)
      keyStep: 15
    });

    // @todo only set up waveform features when waveforms are enabled
    this._waveformsEnabled = this.player.options.ui && this.player.options.ui.waveform;

    if (this._waveformsEnabled) {
      this._waveformLoader = new WaveformLoader();

      // @todo update waveform rendering so that we only need to render a single waveform again
      this._waveformBackground = new Waveform({
        class: 'iono-waveform-background',
        lineWidth: 3,
        color: rgbToCss(this.player.getTheme().getColor('muted-controls'))
      });

      this._waveformBuffered = new Waveform({
        class: 'iono-waveform-buffered',
        lineWidth: 3,
        color: rgbToCss(this.player.getTheme().getColor('primary-muted'))
      });

      this._waveformPosition = new Waveform({
        class: 'iono-waveform-position',
        lineWidth: 3,
        color: rgbToCss(this.player.getTheme().getColor('primary'))
      });
    }

    this.oninput = this.oninput.bind(this);
    this.onchange = this.onchange.bind(this);
    this.onmousemove = this.onmousemove.bind(this);
    this.onplaylistselect = this.onplaylistselect.bind(this);
    this.onempty = this.onempty.bind(this);
    this.ontimeupdate = this.ontimeupdate.bind(this);
    this.ondurationchange = this.ondurationchange.bind(this);
    this.onseekable = this.onseekable.bind(this);
    this.onseeking = this.onseeking.bind(this);
    this.onseeked = this.onseeked.bind(this);
    this.onplay = this.onplay.bind(this);
    this.onpause = this.onpause.bind(this);
    this.onprogress = this.onprogress.bind(this);
    this.onresize = this.onresize.bind(this);
    this.ontexttrackschange = this.ontexttrackschange.bind(this);
  }

  getDefaultProps () {
    return {
      position: 0,
      duration: 0,
      progress: 0,
      seeking: false,
      seekable: false,
      showDuration: true,
      positionMode: 'elapsed',
      forcePositionMode: null,
      slidePosition: 0,
      waveformData: null
    };
  }

  attach (element) {
    super.attach(element);

    this._slider.attach(this.$('.iono-slider'));
    this._slider.$element.addEventListener('input', this.oninput);
    this._slider.$element.addEventListener('change', this.onchange);
    // this._sliderTooltip = createTooltip(this._slider.$element);

    if (this._waveformsEnabled) {
      this._waveformBackground.attach(this.$('.iono-waveform-background'));
      this._waveformPosition.attach(this.$('.iono-waveform-position'));
      this._waveformBuffered.attach(this.$('.iono-waveform-buffered'));
    }

    if (supportsMouseInput()) {
      this._slider.$element.addEventListener('mousemove', this.onmousemove, { passive: true });
    }

    if (supportsTouchInput()) {
      this._slider.$element.addEventListener('touchstart', this.onmousemove, { passive: true });
      this._slider.$element.addEventListener('touchmove', this.onmousemove, { passive: true });
    }

    if (this.player) {
      this.player.on('playlistselect', this.onplaylistselect);
      this.player.on('empty', this.onempty);
      this.player.on('timeupdate', this.ontimeupdate);
      this.player.on('durationchange', this.ondurationchange);
      this.player.on('seekable', this.onseekable);
      this.player.on('seeking', this.onseeking);
      this.player.on('seeked', this.onseeked);
      this.player.on('play', this.onplay);
      this.player.on('pause', this.onpause);
      this.player.on('progress', this.onprogress);
      this.player.on('texttrackschange', this.ontexttrackschange);
    }

    resizeObserver.observe(this.$element);
    this.$element.addEventListener('resize', this.onresize);

    this.update();
  }

  detach () {
    if (this.isAttached()) {
      this._slider.$element.removeEventListener('input', this.oninput);
      this._slider.$element.removeEventListener('change', this.onchange);

      if (supportsMouseInput()) {
        this._slider.$element.removeEventListener('mousemove', this.onmousemove, { passive: true });
      }
      if (supportsTouchInput()) {
        this._slider.$element.removeEventListener('touchmove', this.onmousemove, { passive: true });
      }

      this._slider.detach();
      // this._sliderTooltip.destroy();

      if (this._waveformsEnabled) {
        this._waveformBackground.detach();
        this._waveformPosition.detach();
        this._waveformBuffered.detach();
      }

      resizeObserver.unobserve(this.$element);
      this.$element.removeEventListener('resize', this.onresize);
    }

    if (this.player) {
      this.player.off('playlistselect', this.onplaylistselect);
      this.player.off('empty', this.onempty);
      this.player.off('timeupdate', this.ontimeupdate);
      this.player.off('durationchange', this.ondurationchange);
      this.player.off('seekable', this.onseekable);
      this.player.off('seeking', this.onseeking);
      this.player.off('seeked', this.onseeked);
      this.player.off('play', this.onplay);
      this.player.off('pause', this.onpause);
      this.player.off('progress', this.onprogress);
      this.player.off('texttrackschange', this.ontexttrackschange);
    }

    super.detach();
  }

  render () {
    let waveformsHtml = '';
    if (this._waveformsEnabled) {
      waveformsHtml = `
        <div class="iono-waveforms" hidden>
          <div class="iono-waveform-position-container">
            ${this._waveformPosition.render()}
          </div>
          <div class="iono-waveform-buffered-container">
            ${this._waveformBuffered.render()}
          </div>
          ${this._waveformBackground.render()}
        </div>
      `;
    }

    return `
      <div class="iono-timeline">
        <div class="iono-position">--:--</div>
        <div class="iono-slider iono-timeline-slider" tabindex="0" title="00:00" data-tooltip-follow="x" data-tooltip-trigger="hover">
          <button class="iono-slider-thumb" tabindex="-1"></button>
          ${waveformsHtml}
          <div class="iono-timeline-tracks">
            <div class="iono-slider-track">
              <div class="iono-slider-bar iono-timeline-position"></div>
              <div class="iono-slider-bar iono-timeline-progress"></div>
            </div>
          </div>
        </div>
        ${this.props.showDuration ? '<div class="iono-duration">--:--</div>' : ''}
      </div>
    `;
  }

  update (changes = []) {
    if (!this._slider.isSliding() && !this.props.seeking && !this.player.isLive()) {
      this._slider.setProps({
        value: this.props.position,
        min: 0,
        max: this.props.duration,
        progress: this.props.progress,
        locked: !this.props.seekable
      });
    }

    enableAttribute(this.$element, 'data-seekable', this.props.seekable);

    if (this._waveformsEnabled) {
      const showWaveforms = this.player.isLive() || this.props.waveformData !== null;
      const showBufferedWaveform = showWaveforms && !this.player.isLive();
      const showBackgroundWaveform = showWaveforms && !this.player.isLive();

      enableAttribute(this.$('.iono-waveforms'), 'hidden', !showWaveforms);
      enableAttribute(this.$('.iono-waveform-buffered'), 'hidden', !showBufferedWaveform);
      enableAttribute(this.$('.iono-waveform-background'), 'hidden', !showBackgroundWaveform);
      enableAttribute(this.$('.iono-timeline-tracks'), 'hidden', showWaveforms);

      if (this.props.waveformData && changes.includes('waveformData')) {
        this._waveformPosition.setProp('data', this.props.waveformData);

        if (showBackgroundWaveform) {
          this._waveformBackground.setProp('data', this.props.waveformData);
        }

        if (showBufferedWaveform) {
          this._waveformBuffered.setProp('data', this.props.waveformData);
        }
      }
    }

    const shouldUpdatePosition = [
      'position', 'progress', 'duration', 'showDuration',
      'positionMode', 'slidePosition', 'waveformData'
    ].some(v => changes.includes(v));

    shouldUpdatePosition && this._updatePosition();

    this.updateFeatures();
  }

  updateFeatures (rect = null) {
    const width = (rect || this.$element.getBoundingClientRect()).width;

    enableAttribute(this.$('.iono-timeline-slider'), 'hidden', width < 100);
    enableAttribute(this.$('.iono-duration'), 'hidden', !(width >= 200 && this.player.getDuration() > 0));

    const positionMode = this.props.forcePositionMode || (width < 200 && !this.player.isLive()) ? 'remaining' : 'elapsed';
    if (this.props.positionMode !== positionMode) {
      this.setProps({ positionMode });
    }
  }

  hasWaveforms () {
    return this._waveformsEnabled && (this.player.isLive() || this.player.getPlaylistItem().waveform);
  }

  _updatePosition () {
    const $position = this.$('.iono-position');
    const $duration = this.$('.iono-duration');
    const position = this._slider.isSliding() ? this.props.slidePosition : this.props.position;

    if ($position && (this.props.forcePositionMode || this.props.positionMode) === 'remaining') {
      $position.textContent = '-' + iono.Player.formatSeconds((this.props.duration - position), this.props.duration >= 3600);
      return;
    }

    if ($position) $position.textContent = iono.Player.formatSeconds(position, this.props.duration >= 3600);
    if ($duration) $duration.textContent = iono.Player.formatSeconds(this.props.duration);

    if (this._waveformsEnabled && this.props.waveformData && !this.player.isLive()) {
      const positionPercent = this.props.position / this.props.duration * 100;
      this._waveformPosition.$element.parentNode.style.setProperty('width', `${positionPercent}%`);
      this._waveformBuffered.$element.parentNode.style.setProperty('width', `${this.props.progress}%`);
    } else if (this._waveformsEnabled && this.player.isLive()) {
      this._waveformPosition.$element.parentNode.style.removeProperty('width');
    }
  }

  // UI Events

  onchange (event) {
    const position = this._slider.getValue();

    if (this.player) {
      this.player.setPosition(position);
    }

    this.setProp('position', position);

    const positionChangeEvent = new CustomEvent('positionchange');
    positionChangeEvent.position = position;
    this.$element.dispatchEvent(positionChangeEvent);
  }

  oninput (event) {
    this.props.slidePosition = this._slider.getValue();
    this._updatePosition();
  }

  onmousemove (event) {
    if (this.player.isLive()) return;

    const eventPosition = this._slider._getEventValue(event);
    const formattedPosition = iono.Player.formatSeconds(eventPosition);

    for (const chapter of this.player.getChapters()) {
      if (chapter.startTime <= eventPosition && eventPosition <= chapter.endTime) {
        updateTooltip(this._slider.$element, { text: `${formattedPosition} - ${chapter.text}` });
        // this._sliderTooltip.update({ text: `${formattedPosition} - ${chapter.text}` });
        return;
      }
    }

    updateTooltip(this._slider.$element, { text: formattedPosition });
    // this._sliderTooltip.update({ text: formattedPosition });
  }

  onresize (event) {
    const rect = event.detail.contentRect;

    this.updateFeatures(rect);

    if (this._waveformsEnabled) {
      const waveformClientRect = this.$('.iono-waveforms').getClientRects()[0];

      if (waveformClientRect) {
        const { height, width } = waveformClientRect;

        this._waveformBackground.resize(width, height);
        this._waveformPosition.resize(width, height);
        this._waveformBuffered.resize(width, height);
      }
    }

    if (this.player.isLive()) {
      const $timelineSlider = this.$('.iono-timeline-slider');

      if ($timelineSlider.hasAttribute('hidden') && this._audioAnalyserRunning) {
        this._stopAnalyzer();
      } else if (!$timelineSlider.hasAttribute('hidden') && !this._audioAnalyserRunning) {
        this._startAnalyzer();
      }
    }
  }

  // Playback events

  onplaylistselect (event) {
    // reattach slider if it's busy sliding so that we cancel the current slide
    if (this._slider.isSliding()) {
      this._slider.detach();
      this._slider.attach(this.$('.iono-slider'));
    }

    // suspend audio analyser
    if (!this.player.isLive() && this._audioContext) {
      this._stopAnalyzer();
    }

    if (this.player.isLive()) {
      destroyTooltip(this._slider.$element);
      this._slider.$element.removeAttribute('title');
      // this._sliderTooltip.destroy();
    }
    this.$element.classList.toggle('iono-timeline--live', this.player.isLive());

    let waveformData = null;

    if (this._waveformsEnabled) {
      const playlistItem = event.playlistItem;
      const dataMinMax = [-128, 127];

      if (this._waveformsEnabled) {
        if (playlistItem.waveform) {
          waveformData = playlistItem.waveform.min.map(val => { return normalize(val, dataMinMax); });
        } else {
          const waveformUrl = findWaveformUrl(playlistItem);
          if (waveformUrl) {
            this._waveformLoader.load(waveformUrl)
              .then(waveform => {
                playlistItem.waveform = waveform;
                this.setProp('waveformData', waveform.min.map(val => { return normalize(val, dataMinMax); }));
              })
              .catch(error => {
                // logger.error(`Failed to load waveform: ${error.message}`);
              });
          }
        }
      }
    }

    this.setProps({
      progress: 0,
      position: 0,
      duration: this.player ? this.player.getDuration() : 0,
      seekable: false,
      seeking: false,
      waveformData: waveformData
    });
  }

  onempty (event) {
    this.setProps({
      progress: 0,
      position: 0,
      duration: 0,
      seekable: false,
      seeking: false,
      waveformData: null
    });
  }

  ontimeupdate (event) {
    this.setProp('position', event.position);
  }

  ondurationchange (event) {
    this.setProp('duration', event.duration);
  }

  onseekable (event) {
    this.setProps({
      seekable: true
    });
  }

  onseeking (event) {
    this.setProps({
      seeking: true
    });
  }

  onseeked (event) {
    this.setProps({
      seeking: false
    });
  }

  onplay (event) {
    if (this.player.isLive()) {
      // start audio analyser
      this._startAnalyzer();
    }
  }

  onpause (event) {
    if (this.player.isLive()) {
      // suspend audio analyser
      this._stopAnalyzer();
    }
  }

  onprogress (event) {
    this.setProp('progress', this.player.getBufferedPercentage());
  }

  ontexttrackschange (event) {
    let chapters = [];

    for (const textTrack of event.tracks) {
      if (textTrack.kind === 'chapters' && textTrack.mode === 'showing') {
        chapters = this.player.getChapters();
        break;
      }
    }

    this._slider.setProp('chapters', chapters);
  }

  _shouldRunAnalyzer () {
    // slider must not be hidden
    if (this.$('.iono-timeline-slider').hasAttribute('hidden')) {
      return false;
    }

    // audio source must be live
    if (!this.player.isLive()) {
      return false;
    }

    // audio must be playing
    if (this.player.isPaused()) {
      return false;
    }

    return true;
  }

  _shouldGenerateFakeAnalyserData () {
    // must generate fake data on Safari based browsers
    if (iono.Player.getEnvironment().browser.isSafari) {
      return true;
    }

    // must generate fake data when CORS cannot be used
    if (!this.player.getMedia().crossOrigin) {
      return true;
    }

    return false;
  }

  _startAnalyzer () {
    if (!this._audioAnalyserRunning && this._shouldRunAnalyzer()) {
      if (this._shouldGenerateFakeAnalyserData()) {
        this._currentWaveform = this._generateWaveformData(512);
        this._nextWaveform = this._generateWaveformData(512);
        this._interpolationFactor = 0;
        this._interpolationSpeed = 0.2;
      } else {
        if (!this._audioContext) {
          this._audioContext = new AudioContext();
          this._audioSource = this._audioContext.createMediaElementSource(this.player.getMedia());
          this._audioAnalyser = this._audioContext.createAnalyser();
          this._audioAnalyser.minDecibels = -128.0;
          this._audioAnalyser.maxDecibels = 127.0;
        }

        // connect analyzer node to source node
        this._audioSource.connect(this._audioAnalyser);
        this._audioAnalyser.connect(this._audioContext.destination);
      }

      this._audioAnalyserRunning = true;
      this._renderLoop()(0);
    }
  }

  _stopAnalyzer () {
    if (this._audioAnalyserRunning) {
      if (this._audioContext) {
        // disconnect analyzer node from source node
        this._audioAnalyser.disconnect(this._audioContext.destination);
        this._audioSource.disconnect(this._audioAnalyser);
        // sound will drop unless we reattach the source node to a valid destination
        this._audioSource.connect(this._audioContext.destination);
      }
      this._audioAnalyserRunning = false;
    }
  }

  _updateAnalyzerData () {
    if (!this._audioAnalyserRunning) return;

    if (this._shouldGenerateFakeAnalyserData()) {
      if (this.player.getState() !== 'playing') return;

      const arr = new Array(512);

      let min;
      let max;
      let sum = 0;

      // interpolate waveforms
      for (let i = 0; i < this._currentWaveform.length; i++) {
        const val = arr[i] = this._currentWaveform[i] * (1 - this._interpolationFactor) + this._nextWaveform[i] * this._interpolationFactor;

        // update minmax
        if (typeof min === 'undefined' || val < min) min = val;
        if (typeof max === 'undefined' || val > max) max = val;

        sum += val;
      }

      // update interpolation factor
      this._interpolationFactor += this._interpolationSpeed;

      // check id we have reached the end of interpolation
      if (this._interpolationFactor >= 1) {
        // reset interpolation
        this._interpolationFactor = 0;

        // populate current waveform with the values of the next waveform
        this._currentWaveform = this._nextWaveform;

        // generate a new next waveform
        this._nextWaveform = this._generateWaveformData(512);
      }

      if (this._waveformsEnabled) {
        this.setProp('waveformData', arr);
      } else {
        this._slider.$('.iono-timeline-position').style.transform = `scale(${sum / arr.length / max}, 1)`;
        this._slider.$('.iono-timeline-progress').style.transform = `scale(${max}, 1)`;
      }
    } else {
      const dataArray = new Uint8Array(this._audioAnalyser.frequencyBinCount); // 0 - 255
      this._audioAnalyser.getByteFrequencyData(dataArray);

      const normalizeLen = Math.floor(this._audioAnalyser.frequencyBinCount * (1 - LIVE_WAVEFORM_CUTOFF_PERCENT / 100));

      const normalizedDataArray = Array(normalizeLen).fill(0);

      let min; let max; let sum = 0;
      for (let i = 0; i < normalizeLen; i++) {
        const val = normalizedDataArray[i] = dataArray.at(i);

        // update minmax
        if (typeof min === 'undefined' || val < min) min = val;
        if (typeof max === 'undefined' || val > max) max = val;

        sum += val;
      }

      for (let i = 0; i < normalizeLen; i++) {
        normalizedDataArray[i] = normalize(normalizedDataArray[i], [min, max]);
      }

      if (this._waveformsEnabled) {
        this.setProp('waveformData', normalizedDataArray);
      } else {
        this._slider.$('.iono-timeline-position').style.transform = `scale(${sum / normalizedDataArray.length / max}, 1)`;
        this._slider.$('.iono-timeline-progress').style.transform = `scale(${max / 255}, 1)`;
      }
    }
  }

  _generateWaveformData (len) {
    const arr = new Array(len);

    const frequencies = [3, 6, 12, 24];
    const minAmplitude = 0.4;
    const maxAmplitude = 1.0;

    // pre-fill the waveform data points with the minimum value
    arr.fill(-1);

    // generate waveform data for each frequency, merging them together
    for (const frequency of frequencies) {
      // generateSineNoise(arr, frequency);
      // random amplitude (height of the wave) between 0.4 and 1.0
      const amplitude = Math.random() * (maxAmplitude - minAmplitude) + minAmplitude;

      // random phase (width od the wave)
      const phase = Math.random() * Math.PI * 2;

      for (let i = 0; i < arr.length; i++) {
        const x = i / arr.length * Math.PI * 2;

        // generate random data point value along a wave
        let y = (Math.random() * 2 - 1) * (amplitude * Math.sin(frequency * x + phase));

        // add random sharp spikes for dynamic peaks
        if (Math.random() < 0.02) {
          y += Math.random() * 2 - 1;
        }

        // add some random noise
        y += Math.random() * -0.025 + 0.05;

        // merge with current waveform data point value
        y = arr[i] + y;

        // clamp to [-1, 1]
        y = Math.max(-1, Math.min(1, y));

        // update waveform data point
        arr[i] = y;
      }
    }

    // determine the minimum and maximum values along the waveform
    let min = null; let max = null;
    for (let i = 0; i < arr.length; i++) {
      min = min === null ? arr[i] : Math.min(min, arr[i]);
      max = max === null ? arr[i] : Math.max(max, arr[i]);
    }

    // normalize the waveform to between the minimum and maximum
    for (let i = 0; i < arr.length; i++) {
      arr[i] = (arr[i] - min) / (max - min);
    }

    return arr;
  }

  _renderLoop () {
    let lastUpdateTime = 0;

    const renderLoop = timestamp => {
      if (this._audioAnalyserRunning) requestAnimationFrame(renderLoop);

      const delta = timestamp - lastUpdateTime;

      if (FPS_LIMIT && delta < (1000 / FPS_LIMIT) - FPS_LIMIT_TOLERANCE) {
        return;
      }

      this._updateAnalyzerData();

      lastUpdateTime = timestamp;
    };

    return renderLoop;
  }
}
